import { lazyWithRetry } from '@lib/lazyWithRetry'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import RouterErrorPage from '@features/errors/RouterErrorPage'
import { DashboardLayout } from '@components/layout'
import { ProtectedRoute } from './ProtectedRoute'
import { SuspenseWrapper } from './SuspenseWrapper'

// Lazy load pages
const LoginPage = lazyWithRetry(() => import('@features/auth/LoginPage'))
const SignupPage = lazyWithRetry(() => import('@features/auth/SignupPage'))
const ChangePasswordPage = lazyWithRetry(() => import('@features/auth/ChangePasswordPage'))
const ForgotPasswordPage = lazyWithRetry(() => import('@features/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazyWithRetry(() => import('@features/auth/ResetPasswordPage'))
const DashboardPage = lazyWithRetry(() => import('@features/dashboard/DashboardPage'))
const PortfolioPage = lazyWithRetry(() => import('@features/portfolio/PortfolioPage'))
const PortfolioDetailPage = lazyWithRetry(() => import('@features/portfolio/PortfolioDetailPage'))
const AnalyticsPage = lazyWithRetry(() => import('@features/analytics/AnalyticsPage'))
const LayersPage = lazyWithRetry(() => import('@features/layers/LayersPage'))
const ReportsPage = lazyWithRetry(() => import('@features/reports/ReportsPage'))
const SettingsPage = lazyWithRetry(() => import('@features/settings/SettingsPage'))
const NotFoundPage = lazyWithRetry(() => import('@features/errors/NotFoundPage'))
const ServerErrorPage = lazyWithRetry(() => import('@features/errors/ServerErrorPage'))
const MaintenancePage = lazyWithRetry(() => import('@features/errors/MaintenancePage'))

// Docs pages
const DocsLayout = lazyWithRetry(() => import('@features/docs/layouts/DocsLayout').then(m => ({ default: m.DocsLayout })))
const ApiDocPage = lazyWithRetry(() => import('@features/docs/pages/ApiDocPage'))
const ModuleDocPage = lazyWithRetry(() => import('@features/docs/pages/ModuleDocPage'))

export const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouterErrorPage />,
    children: [
      // Auth routes (public)
      {
        path: '/login',
        element: (
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/signup',
        element: (
          <SuspenseWrapper>
            <SignupPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/change-password',
        element: (
          <SuspenseWrapper>
            <ChangePasswordPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <SuspenseWrapper>
            <ForgotPasswordPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/reset-password',
        element: (
          <SuspenseWrapper>
            <ResetPasswordPage />
          </SuspenseWrapper>
        ),
      },

      // Protected routes
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              { path: 'dashboard', element: <DashboardPage /> },
              { path: 'portfolio', element: <PortfolioPage /> },
              { path: 'portfolio/:id', element: <PortfolioDetailPage /> },
              { path: 'analytics', element: <AnalyticsPage /> },
              { path: 'layers', element: <LayersPage /> },
              { path: 'reports', element: <ReportsPage /> },
              { path: 'settings', element: <SettingsPage /> },
            ],
          },
          // Docs Layout (Peer to DashboardLayout)
          {
            path: 'docs',
            element: <DocsLayout />,
            children: [
              { index: true, element: <Navigate to="/docs/api" replace /> },
              { path: 'api', element: <ApiDocPage /> },
              { path: 'modules/:id', element: <ModuleDocPage /> },
            ]
          },
        ],
      },

      // Error routes
      {
        path: '/500',
        element: (
          <SuspenseWrapper>
            <ServerErrorPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '/503',
        element: (
          <SuspenseWrapper>
            <MaintenancePage />
          </SuspenseWrapper>
        ),
      },

      // 404 catch-all
      {
        path: '*',
        element: (
          <SuspenseWrapper>
            <NotFoundPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },
])
